import { useState, useEffect, useMemo } from "react";
import { db } from "../../firebase";
import { collection, query, where, getDocs, doc, setDoc, updateDoc, getDoc as firestoreGetDoc } from "firebase/firestore";

/*
  useBusParkFirebase(total, labels)

  - labels: optional array of place labels in order (e.g. ["A1","A2",...,"O12"])
  - parking array index corresponds to labels[index] (or numeric index if no labels)
  - place is always treated/written as string ("" for unassigned)
*/

export function useBusParkFirebase(total = 79, labels = []) {
  const totalSlots = (Array.isArray(labels) && labels.length) ? labels.length : total;
  const [docs, setDocs] = useState([]);
  const [parking, setParking] = useState(Array(totalSlots).fill(null));
  const [loading, setLoading] = useState(true);

  // keep a stable reference to initialized firestore db
  const dbInstance = db;

  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      try {
        const q = await getDocs(collection(dbInstance, "BusParkPlaces"));
        const rawDocs = q.docs.map(d => ({ id: d.id, ...d.data() }));

        const maxSlots = Math.max((Array.isArray(labels) ? labels.length : 0), total);
        const parkingArr = Array.from({ length: maxSlots }, () => null);

        rawDocs.forEach(docEntry => {
          let idx = null;
          const placeVal = docEntry.place;
          if (placeVal !== undefined && placeVal !== null && String(placeVal).trim() !== "") {
            const asNum = Number(placeVal);
            if (!Number.isNaN(asNum)) {
              idx = asNum - 1;
            } else if (Array.isArray(labels) && labels.length) {
              const s = String(placeVal);
              const labelIdx = labels.indexOf(s);
              if (labelIdx >= 0) idx = labelIdx;
            }
          } else if (docEntry.index !== undefined && !Number.isNaN(Number(docEntry.index))) {
            idx = Number(docEntry.index);
          } else if (docEntry.pos !== undefined && !Number.isNaN(Number(docEntry.pos))) {
            idx = Number(docEntry.pos);
          } else if (!Number.isNaN(parseInt(docEntry.id, 10))) {
            idx = parseInt(docEntry.id, 10) - 1;
          }

          if (idx === null || idx < 0 || idx >= maxSlots) return;

          const busVal = docEntry.bus !== undefined ? docEntry.bus : (docEntry.number ?? docEntry.value ?? docEntry.label ?? null);
          const bus = busVal !== null && busVal !== undefined ? String(busVal) : null;
          const status = Number.isFinite(Number(docEntry.status)) ? Number(docEntry.status) : 0;

          parkingArr[idx] = { bus, status, _raw: docEntry };
        });

        if (!mounted) return;
        setDocs(rawDocs);
        setParking(parkingArr.slice(0, Math.max(0, totalSlots)));
      } catch (err) {
        console.error("useBusParkFirebase fetch error:", err);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchData();
    return () => { mounted = false; };
  }, [total, dbInstance, labels]);

  const BUS_NUMBERS = useMemo(() => {
    if (!docs || docs.length === 0) return [];
    return Array.from(new Set(docs.map(d => d.bus).filter(Boolean)));
  }, [docs]);

  const FREE_BUS_NUMBERS = useMemo(() => {
    if (!docs || docs.length === 0) return [];
    return Array.from(new Set(
      docs
        .filter(d => {
          const p = d.place;
          return p === "" || p === 0 || p === "0" || p === undefined || p === null;
        })
        .map(d => d.bus)
        .filter(Boolean)
    ));
  }, [docs]);

  // compute available numbers for a given slot: candidates not assigned elsewhere (but allow current slot's value)
  const getAvailableNumbers = (parkingArr, editingIndex) => {
    const assigned = (parkingArr || [])
      .map((entry, idx) => {
        if (idx === editingIndex) return null;
        if (!entry) return null;
        return typeof entry === "object" ? (entry.bus ?? entry.number ?? String(entry)) : String(entry);
      })
      .filter(Boolean);

    const candidates = [...FREE_BUS_NUMBERS];

    const editingEntry = parkingArr?.[editingIndex];
    const editingBus = editingEntry
      ? (typeof editingEntry === "object" ? (editingEntry.bus ?? editingEntry.number ?? null) : editingEntry)
      : null;
    if (editingBus && !candidates.includes(String(editingBus))) candidates.push(String(editingBus));

    return candidates.filter(num => !assigned.includes(num));
  };

  const findDocByBus = (busValue) => {
    const busStr = String(busValue ?? "");
    return (docs || []).find(d =>
      (d && d.bus !== undefined && String(d.bus) === busStr) ||
      (d && d.id !== undefined && String(d.id) === busStr)
    );
  };

  const docPlaceMatchesIndex = (docPlace, index) => {
    if (docPlace === undefined || docPlace === null || String(docPlace).trim() === "") return false;
    const asNum = Number(docPlace);
    if (!Number.isNaN(asNum)) return (asNum - 1) === index;
    if (Array.isArray(labels) && labels.length) return String(docPlace) === String(labels[index]);
    return false;
  };

  // assign bus to slot index (place as string)
  async function handleSelect(index, payloadOrValue) {
    // payloadOrValue can be { bus, place, status?, info? } or just bus string
    const payload = (payloadOrValue && typeof payloadOrValue === "object")
      ? { ...payloadOrValue }
      : { bus: String(payloadOrValue ?? ""), place: "" };

    payload.place = payload.place !== undefined && payload.place !== null ? String(payload.place) : "";

    try {
      const colRef = collection(dbInstance, "BusParkPlaces");

      const busQ = query(colRef, where("bus", "==", String(payload.bus)));
      const busSnap = await getDocs(busQ);
      const serverDoc = !busSnap.empty ? busSnap.docs[0] : null;
      const serverData = serverDoc ? serverDoc.data() : null;

      const localDoc = findDocByBus(payload.bus);
      const existingId = serverDoc ? serverDoc.id : (localDoc ? localDoc.id : null);
      const existingData = serverData ?? (localDoc ?? null);

      const placeQ = query(colRef, where("place", "==", String(payload.place)));
      const placeSnap = await getDocs(placeQ);
      const targetPlaceDoc = !placeSnap.empty ? placeSnap.docs[0] : null;
      const targetPlaceData = targetPlaceDoc ? targetPlaceDoc.data() : null;

      const existingStatus = existingData && existingData.status !== undefined ? Number(existingData.status) : undefined;
      const existingInfo = existingData && existingData.info !== undefined ? String(existingData.info) : undefined;

      if (existingId) {
        if (targetPlaceDoc && targetPlaceDoc.id !== existingId) {
          await updateDoc(doc(dbInstance, "BusParkPlaces", targetPlaceDoc.id), { place: "" });
        }

        const updatePayload = { place: String(payload.place) };
        if (payload.bus !== undefined) updatePayload.bus = payload.bus;
        if (payload.status !== undefined) updatePayload.status = Number(payload.status);
        if (payload.info !== undefined) updatePayload.info = String(payload.info);

        await updateDoc(doc(dbInstance, "BusParkPlaces", existingId), updatePayload);

        setParking(prev => {
          const next = Array.isArray(prev) ? [...prev] : Array.from({ length: totalSlots }, () => null);
          for (let i = 0; i < next.length; i++) {
            if (i !== index && next[i] && String(next[i].bus) === String(payload.bus)) next[i] = null;
          }
          next[index] = {
            bus: payload.bus,
            place: payload.place,
            status: updatePayload.status !== undefined ? updatePayload.status : (existingStatus ?? 0),
            info: updatePayload.info !== undefined ? updatePayload.info : (existingInfo ?? "")
          };
          return next;
        });

        setDocs(prev => (prev || []).map(d => {
          if (!d) return d;
          if (d.id === existingId) return {
            ...d,
            place: payload.place,
            bus: payload.bus,
            status: updatePayload.status !== undefined ? updatePayload.status : d.status,
            info: updatePayload.info !== undefined ? updatePayload.info : d.info
          };
          if (targetPlaceDoc && d.id === targetPlaceDoc.id) return { ...d, place: "" };
          return d;
        }));
      } else {
        if (targetPlaceDoc) {
          await updateDoc(doc(dbInstance, "BusParkPlaces", targetPlaceDoc.id), { place: "" });
        }

        const newId = String(payload.bus);
        const toWrite = { bus: payload.bus, place: String(payload.place) };
        if (payload.status !== undefined) toWrite.status = Number(payload.status);
        if (payload.info !== undefined) toWrite.info = String(payload.info);

        await setDoc(doc(dbInstance, "BusParkPlaces", newId), toWrite, { merge: true });

        setParking(prev => {
          const next = Array.isArray(prev) ? [...prev] : Array.from({ length: totalSlots }, () => null);
          for (let i = 0; i < next.length; i++) {
            if (next[i] && String(next[i].bus) === String(payload.bus)) next[i] = null;
          }
          next[index] = {
            bus: payload.bus,
            place: payload.place,
            status: toWrite.status !== undefined ? toWrite.status : (existingStatus ?? 0),
            info: toWrite.info !== undefined ? toWrite.info : (existingInfo ?? "")
          };
          return next;
        });

        setDocs(prev => {
          const next = prev ? [...prev] : [];
          if (targetPlaceDoc) {
            const idx = next.findIndex(d => d.id === targetPlaceDoc.id);
            if (idx >= 0) next[idx] = { ...targetPlaceData, id: targetPlaceDoc.id, place: "" };
          }
          const existsIdx = next.findIndex(d => d.id === newId || String(d.bus) === newId);
          const newDocEntry = { id: newId, bus: payload.bus, place: payload.place, ...(toWrite.status !== undefined ? { status: toWrite.status } : {}), ...(toWrite.info !== undefined ? { info: toWrite.info } : {}) };
          if (existsIdx >= 0) next[existsIdx] = { ...next[existsIdx], ...newDocEntry }; else next.push(newDocEntry);
          return next;
        });
      }

      const writtenId = existingId || String(payload.bus);
      try {
        const serverSnap = await firestoreGetDoc(doc(dbInstance, "BusParkPlaces", writtenId));
        console.log("handleSelect: server doc after write:", writtenId, serverSnap.exists() ? serverSnap.data() : "NOT_FOUND");
      } catch (errRead) {
        console.error("handleSelect: failed to read back doc", errRead);
      }

      const usedStatus = payload.status !== undefined ? Number(payload.status) : (existingStatus !== undefined ? existingStatus : undefined);
      const usedInfo = payload.info !== undefined ? String(payload.info) : (existingInfo !== undefined ? existingInfo : undefined);

      return { ok: true, payload: { bus: payload.bus, place: payload.place, status: usedStatus, info: usedInfo } };
    } catch (err) {
      console.error("handleSelect error:", err);
      return { ok: false, message: err && err.message ? err.message : String(err) };
    }
  }

  const handleToggleStatus = async (index) => {
    try {
      let found = (docs || []).find(d => docPlaceMatchesIndex(d.place, index));

      if (!found) {
        const entry = parking?.[index];
        const busStr = entry && typeof entry === "object" ? String(entry.bus ?? "") : String(entry ?? "");
        if (busStr) found = (docs || []).find(d => String(d.bus) === busStr);
      }

      if (!found || !found.id) return { ok: false, message: "doc not found" };

      const currentStatus = Number.isFinite(Number(found.status)) ? Number(found.status) : 0;
      const nextStatus = currentStatus === 2 ? 0 : currentStatus + 1;

      await updateDoc(doc(dbInstance, "BusParkPlaces", found.id), { status: nextStatus });

      setDocs(prev => (prev || []).map(d => (d && d.id === found.id ? { ...d, status: nextStatus } : d)));
      setParking(prev => {
        const next = Array.isArray(prev) ? [...prev] : Array.from({ length: totalSlots }, () => null);
        if (next[index] && (next[index].bus == found.bus || String(next[index].bus) === String(found.bus))) {
          next[index] = { ...next[index], status: nextStatus };
        } else {
          const fdPlace = found.place;
          const asNum = Number(fdPlace);
          if (!Number.isNaN(asNum) && asNum - 1 >= 0 && asNum - 1 < next.length) {
            next[asNum - 1] = { bus: found.bus, place: fdPlace, status: nextStatus };
          } else if (Array.isArray(labels) && labels.length) {
            const idx = labels.indexOf(String(fdPlace));
            if (idx >= 0 && idx < next.length) next[idx] = { bus: found.bus, place: String(fdPlace), status: nextStatus };
          }
        }
        return next;
      });

      return { ok: true, status: nextStatus };
    } catch (err) {
      console.error("handleToggleStatus error:", err);
      throw err;
    }
  };

  const handleDelete = async (index) => {
    setParking(prev => {
      const next = Array.isArray(prev) ? [...prev] : Array.from({ length: totalSlots }, () => null);
      next[index] = null;
      return next;
    });

    try {
      let found = (docs || []).find(d => docPlaceMatchesIndex(d.place, index));

      if (!found) {
        const entry = parking?.[index];
        const busStr = entry && typeof entry === "object" ? String(entry.bus ?? "") : String(entry ?? "");
        if (busStr) found = (docs || []).find(d => String(d.bus) === busStr);
      }

      if (!found || !found.id) return { ok: false, message: "doc not found" };

      await updateDoc(doc(dbInstance, "BusParkPlaces", found.id), { place: "" });

      setDocs(prev => (prev || []).map(d => (d && d.id === found.id ? { ...d, place: "" } : d)));

      return { ok: true };
    } catch (err) {
      console.error("handleDelete failed:", err);
      throw err;
    }
  };

  return {
    docs,
    parking,
    setParking,
    loading,
    BUS_NUMBERS,
    getAvailableNumbers,
    handleDelete,
    handleSelect,
    handleToggleStatus,
  };
}